import { RefObject, useEffect, useRef } from 'react';

const ROW_TOP_TOLERANCE_PX = 4;

const resetHeaderHeights = (headers: HTMLElement[]): void => {
  headers.forEach(header => {
    header.style.minHeight = '';
  });
};

const groupHeadersByRow = (cards: HTMLElement[]): Map<number, HTMLElement[]> => {
  const rows = new Map<number, HTMLElement[]>();

  cards.forEach(card => {
    const header = card.querySelector<HTMLElement>('.specimen-card__header');
    if (!header) return;

    const top = Math.round(card.getBoundingClientRect().top);
    let rowKey = top;

    Array.from(rows.keys()).some(existingTop => {
      if (Math.abs(existingTop - top) <= ROW_TOP_TOLERANCE_PX) {
        rowKey = existingTop;
        return true;
      }
      return false;
    });

    const headersInRow = rows.get(rowKey) ?? [];
    headersInRow.push(header);
    rows.set(rowKey, headersInRow);
  });

  return rows;
};

const equalizeSpecimenCardHeaders = (container: HTMLElement | null, isEqualizingRef: { current: boolean }): void => {
  if (!container || isEqualizingRef.current) return;

  const cards = Array.from(container.querySelectorAll<HTMLElement>(':scope > .specimen-card'));
  if (cards.length === 0) return;

  const headers = cards
    .map(card => card.querySelector<HTMLElement>('.specimen-card__header'))
    .filter((header): header is HTMLElement => header !== null);

  isEqualizingRef.current = true;
  try {
    resetHeaderHeights(headers);

    const rows = groupHeadersByRow(cards);
    rows.forEach(headersInRow => {
      const maxHeight = Math.max(...headersInRow.map(header => header.getBoundingClientRect().height));
      if (!Number.isFinite(maxHeight) || maxHeight <= 0) return;

      const nextMinHeight = `${Math.ceil(maxHeight)}px`;
      headersInRow.forEach(header => {
        if (header.style.minHeight !== nextMinHeight) {
          header.style.minHeight = nextMinHeight;
        }
      });
    });
  } finally {
    isEqualizingRef.current = false;
  }
};

type UseEqualizeSpecimenCardHeadersOptions = {
  containerRef: RefObject<HTMLElement | null>;
  enabled: boolean;
  /** Re-run when list content or loading state changes. */
  itemCount: number;
  isLoading: boolean;
};

/**
 * Equalizes `.specimen-card__header` heights for cards that share a visual row,
 * so attributes/breeder sections stay aligned when names wrap or tags stack.
 */
export const useEqualizeSpecimenCardHeaders = ({
  containerRef,
  enabled,
  itemCount,
  isLoading,
}: UseEqualizeSpecimenCardHeadersOptions): void => {
  const equalizeFrameIdRef = useRef<number | null>(null);
  const isEqualizingRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const scheduleEqualization = (): void => {
      if (!containerRef.current) return;

      if (equalizeFrameIdRef.current !== null) {
        cancelAnimationFrame(equalizeFrameIdRef.current);
      }

      equalizeFrameIdRef.current = requestAnimationFrame(() => {
        equalizeFrameIdRef.current = requestAnimationFrame(() => {
          equalizeFrameIdRef.current = null;
          equalizeSpecimenCardHeaders(containerRef.current, isEqualizingRef);
        });
      });
    };

    scheduleEqualization();

    const handleResize = (): void => {
      scheduleEqualization();
    };

    window.addEventListener('resize', handleResize);

    const container = containerRef.current;
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' && container
        ? new ResizeObserver(() => {
            scheduleEqualization();
          })
        : null;

    if (resizeObserver && container) {
      resizeObserver.observe(container);
    }

    let fontsReadyCancelled = false;
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      void document.fonts.ready.then(() => {
        if (!fontsReadyCancelled) {
          scheduleEqualization();
        }
      });
    }

    return () => {
      fontsReadyCancelled = true;
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();

      if (equalizeFrameIdRef.current !== null) {
        cancelAnimationFrame(equalizeFrameIdRef.current);
        equalizeFrameIdRef.current = null;
      }
    };
  }, [containerRef, enabled, itemCount, isLoading]);
};
